import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';
import CountUp from 'react-countup';
import { toast } from 'react-hot-toast';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { TriangleAlert } from 'lucide-react';
import { FaYoutube } from 'react-icons/fa';
import axios from 'axios';
import EligibilityScorecardSection from '../EligibilityScorecard/EligibilityScorecardSection';

import {
  DocumentCheckIcon,
  ClockIcon,
  ShieldCheckIcon,
  MapPinIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  CheckCircleIcon,
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  ChartBarIcon,
  TrophyIcon,
  GlobeAltIcon,
  BanknotesIcon,
  HomeIcon,
  BuildingOffice2Icon,
  GlobeAsiaAustraliaIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CalculatorIcon,
  ChevronRightIcon as ChevronRightIconOutline
} from '@heroicons/react/24/outline';

import {
  StarIcon as StarIconSolid,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/solid';

import { formatCurrency, debounce } from '../../utils/helpers';
import CreditScoreModal from '../Modals/CreditScoreModal';
import CreditScoreModal2 from '../Modals/CreditScoreModal2';
import SEO from '../SEO/SEO';

// -------------------------------------------------------------
// 1. Static Important News Section
// -------------------------------------------------------------
const StaticImportantNewsSection = () => {
  const navigate = useNavigate();
  const messages = [
    { message: "Security Alert: Fraudsters are misusing the name of Aham Housing Finance." },
    { message: "Do not share personal information or transfer money to unknown persons." },
    { message: "Verify all offers only through our official website or branch." },
    { message: "Stay alert and report suspicious calls immediately." }
  ];

  return (
    <div
      id="important-news"
      onClick={() => navigate("/home-loan-blog-ham-housing-finance")}
      className="bg-red-100 border-y border-red-300 py-3 overflow-hidden cursor-pointer"
    >
      <div className="container mx-auto px-4 flex items-center gap-3">
        <TriangleAlert className="text-yellow-700 w-5 h-5 flex-shrink-0" />
        <div className="relative w-full overflow-hidden">
          <motion.div
            className="flex gap-16 whitespace-nowrap"
            animate={{ x: ["100%", "-100%"] }}
            transition={{
              repeat: Infinity,
              duration: 50,
              ease: "linear",
            }}
          >
            {[...messages, ...messages].map((msg, idx) => (
              <p key={idx} className="text-red-600 font-medium text-sm md:text-base">
                {msg.message}
              </p>
            ))}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
// 2. Static Hero/Banner Section
// -------------------------------------------------------------
const StaticHeroSection = () => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const [bannerData, setBannerData] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/banners/active?page_type=scorecard_page');
        if (response.data.success && response.data.data.length > 0) {
          setBannerData(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      }
    };
    fetchBanner();
  }, []);

  const defaultBanner = {
    title: isTamil ? "அஹம் ஸ்கோர்கார்டு மூலம் உங்கள் வீட்டு உபயோகக் கடன் தகுதியை உடனடியாக மதிப்பிடுங்கள்" : "Assess Your Home Loan Eligibility Instantly with Aham Scorecard",
    subtitle: isTamil ? "சம்பளச் சீட்டு அல்லது ஐடிஆர் (ITR) இல்லையா? கவலை வேண்டாம். உங்கள் தகுதியைக் கணக்கிடவும் உங்கள் கனவு இல்லத்தை நோக்கிய முதல் அடியை எடுத்து வைக்கவும் எங்களின் உடனடி சுயமதிப்பீட்டு ஸ்கோர்கார்டைப் பயன்படுத்தவும்." : "No salary slips or ITR? No problem. Use our instant, self-assessment scorecard to calculate your eligibility and take the first step towards your dream home.",
    // backgroundImage: "/img/Home_banner/scorecard_hero_bg.png",
    backgroundImage: "/img/Home_banner/Scorecard.jpeg",
    primary_button_text: isTamil ? "உங்கள் மதிப்பெண்ணை இப்போது சரிபார்க்கவும்" : "Check Your Score Now",
    primary_button_link: "#scorecard"
  };

  const banner = bannerData || defaultBanner;

  const handleScrollToScorecard = (e) => {
    e.preventDefault();
    const element = document.getElementById('scorecard');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative h-screen max-h-[800px] min-h-[65vh] overflow-hidden bg-gray-900">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${banner.image_url || banner.backgroundImage})`,
        }}
        role="img"
        aria-label={isTamil ? "அஹம் வீட்டு வசதி நிதி - கடன் தகுதி மதிப்பீட்டு கார்டு" : "Aham Housing Finance - Eligibility Scorecard Assessment dashboard background"}
      >
        <img
          src={banner.image_url || banner.backgroundImage}
          alt={isTamil ? "அஹம் வீட்டு வசதி நிதி - கடன் தகுதி மதிப்பீட்டு கார்டு" : "Aham Housing Finance - Eligibility Scorecard Assessment dashboard background"}
          className="hidden"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-start pb-32 sm:pb-0">
        <div className="max-w-4xl w-full text-left">
          <div className="space-y-6 flex flex-col items-start">
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-7xl font-extrabold leading-tight text-white"
            >
              {banner.title}
            </motion.h1>

            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-2xl text-white/90 max-w-2xl font-medium leading-relaxed"
            >
              {banner.subtitle}
            </motion.p>

            {/* Highlights Display / Fact Sheet */}
            <div className="flex flex-wrap gap-3 pt-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2.5 rounded-full shadow-lg"
              >
                <ShieldCheckIcon className="h-5 w-5 text-yellow-400" />
                <span className="text-white font-semibold text-xs md:text-sm">
                  {isTamil ? "100% இலவச சரிபார்ப்பு" : "100% Free Check"}
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2.5 rounded-full shadow-lg"
              >
                <ClockIcon className="h-5 w-5 text-yellow-400" />
                <span className="text-white font-semibold text-xs md:text-sm">
                  {isTamil ? "2 நிமிடங்களில் முடிவு" : "Takes Only 2 Mins"}
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2.5 rounded-full shadow-lg"
              >
                <DocumentCheckIcon className="h-5 w-5 text-yellow-400" />
                <span className="text-white font-semibold text-xs md:text-sm">
                  {isTamil ? "ITR அல்லது சம்பளச் சீட்டு தேவையில்லை" : "No ITR / Salary Slip Needed"}
                </span>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col items-start sm:flex-row gap-4 pt-4 w-full sm:w-auto"
            >
              {banner.primary_button_text && (
                <a
                  href={banner.primary_button_link}
                  onClick={handleScrollToScorecard}
                  className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto sm:mx-0 animate-bounce"
                >
                  {banner.primary_button_text}
                </a>
              )}
            </motion.div>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="flex items-center space-x-2 text-sm font-medium pt-2"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                {isTamil ? "முகப்பு" : "Home"}
              </Link>
              <ChevronRightIconOutline className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">
                {isTamil ? "தகுதி கார்டு" : "Scorecard"}
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6 }}
        className="absolute bottom-8 right-8 z-20"
      >
        <div className="flex flex-col items-center space-y-2 text-white/80">
          <span className="text-xs font-medium uppercase tracking-wider">Scroll</span>
          <div className="w-px h-8 bg-white/60 relative">
            <motion.div
              animate={{ y: [0, 16, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute top-0 left-0 w-px h-2 bg-white"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
};

// -------------------------------------------------------------
// 2.5. Static Why Choose Section
// -------------------------------------------------------------
const StaticWhyChooseSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [settings, setSettings] = useState({
    section_title: 'Why Homeowners Choose Aham Housing Finance',
    image_url: 'img/Scorecard/Aham.png',
    image_title: 'Your Dream Home, Made Reality',
    image_subtitle: 'Experience a hassle-free loan process tailored for you.'
  });
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);

  const IconMap = {
    DocumentCheckIcon,
    UsersIcon,
    MapPinIcon,
    BanknotesIcon,
    ShieldCheckIcon,
    ClockIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
    HomeIcon
  };

  const defaultReasons = [
    { title: "Flexible assessment", description: "not just a payslip check.", icon_name: "DocumentCheckIcon" },
    { title: "Family income counted", description: "for a bigger loan.", icon_name: "UsersIcon" },
    { title: "Doorstep service", description: "our team comes to you.", icon_name: "MapPinIcon" },
    { title: "Clear fund release", description: "stage by stage.", icon_name: "BanknotesIcon" },
    { title: "Plain language", description: "no surprises.", icon_name: "ShieldCheckIcon" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, reasonsRes] = await Promise.all([
          fetch('/api/admin/eligibility-scorecard/why-choose/settings'),
          fetch('/api/admin/eligibility-scorecard/why-choose/reasons/active')
        ]);
        const settingsJson = await settingsRes.json();
        const reasonsJson = await reasonsRes.json();

        if (settingsJson.success && settingsJson.data) {
          setSettings(settingsJson.data);
        }
        if (reasonsJson.success && reasonsJson.data.length > 0) {
          setReasons(reasonsJson.data);
        } else {
          setReasons(defaultReasons);
        }
      } catch (error) {
        console.error('Error fetching why choose scorecard data:', error);
        setReasons(defaultReasons);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 100, damping: 15 } }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden border-t border-gray-100">
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left Side: Image / Graphics */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2 relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl aspect-[4/5] md:aspect-square lg:aspect-[4/5] group">
              <img
                src={settings.image_url ? (settings.image_url.startsWith('http') ? settings.image_url : `/${settings.image_url}`) : "/img/Scorecard/Aham.png"}
                alt="Beautiful home exterior"
                className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1D36]/90 via-[#0A1D36]/20 to-transparent"></div>

              <div className="absolute bottom-10 left-10 right-10">
                <h3 className="text-3xl font-bold text-white mb-2 transform translate-y-4 group-hover:translate-y-0 transition duration-500">
                  {settings.image_title.split(',').length > 1 ? (
                    <>
                      {settings.image_title.split(',')[0]}, <br />
                      <span className="text-[#D4AF37]">{settings.image_title.split(',').slice(1).join(',')}</span>
                    </>
                  ) : (
                    settings.image_title
                  )}
                </h3>
                <p className="text-gray-200 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition duration-500 delay-100">
                  {settings.image_subtitle}
                </p>
              </div>
            </div>

            {/* Decorative block */}
            <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#D4AF37] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
            <div className="absolute -top-8 -left-8 w-48 h-48 bg-primary-600 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '2s' }}></div>
          </motion.div>

          {/* Right Side: Features */}
          <motion.div
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
            className="w-full lg:w-1/2"
          >
            <motion.div variants={itemVariants} className="mb-10">
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
                {settings.section_title.split('Aham Housing Finance').length > 1 ? (
                  <>
                    {settings.section_title.split('Aham Housing Finance')[0]} <br />
                    <span className="text-primary-600">Aham Housing Finance</span>
                  </>
                ) : (
                  settings.section_title
                )}
              </h2>
              <div className="w-20 h-1.5 bg-[#D4AF37] rounded-full"></div>
            </motion.div>

            <div className="space-y-4">
              {reasons.map((reason, idx) => {
                const IconComponent = IconMap[reason.icon_name] || DocumentCheckIcon;
                return (
                  <motion.div
                    key={reason.id || idx}
                    variants={itemVariants}
                    whileHover={{ x: 10 }}
                    className="flex items-start group p-4 rounded-2xl hover:bg-gray-50 transition-all duration-300 border border-transparent hover:border-gray-100 hover:shadow-soft cursor-default"
                  >
                    <div className="flex-shrink-0 mr-6 mt-1">
                      <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center group-hover:bg-primary-600 text-primary-600 group-hover:text-white transition-all duration-300 shadow-sm group-hover:shadow-md group-hover:rotate-6">
                        <IconComponent className="w-7 h-7" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">{reason.title}</h4>
                      <p className="text-gray-600 text-lg leading-relaxed">{reason.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 3. Static Eligibility Section
// -------------------------------------------------------------
const StaticEligibilityCardsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const isTamil = i18n.language === 'ta';

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionSettings, setSectionSettings] = useState({
    title: isTamil ? "சம்பளத்தின் அடிப்படையில் வீட்டுக் கடன் தகுதி கால்குலேட்டர்" : "Home Loan Eligibility Calculator Based on Salary",
    subtitle: isTamil ? "உங்கள் மாத வருமானம் தான் வீட்டுக் கடன் தகுதியை நிர்ணயிக்கும் முக்கிய காரணியாகும். அஹம் HFC சம்பளம் பெறுபவர்கள் மற்றும் சுய தொழில் புரிபவர்கள் ஆகிய இரு தரப்பினருக்கும் உதவுகிறது." : "Your monthly income is the single biggest factor in a home loan eligibility calculator based on salary. Aham HFC works with both salaried and self-employed applicants, and the income assessment looks a little different for each."
  });

  const IconMap = {
    BuildingOfficeIcon,
    ChartBarIcon,
    GlobeAltIcon
  };

  useEffect(() => {
    const defaultCriteria = [
      {
        id: 1,
        iconName: 'BuildingOfficeIcon',
        title: isTamil ? "சம்பளம் பெறுபவர்கள்" : "Salaried",
        color: "text-blue-600",
        bgColor: "bg-blue-50",
        points: [
          isTamil ? "குறைந்தபட்ச வருமானம் மாதம் ₹15,000." : "Minimum income is ₹15,000 a month.",
          isTamil ? "பணச் சம்பளம் மாதம் ₹20,000 வரை கணக்கிடப்படுகிறது." : "Cash salary is counted up to ₹20,000 a month.",
          isTamil ? "உங்கள் தற்போதைய பணியில் 2 வருடங்கள் இருக்க வேண்டும்." : "We also look for 2 years in your current job."
        ]
      },
      {
        id: 2,
        iconName: 'ChartBarIcon',
        title: isTamil ? "சுய தொழில் புரிபவர்கள்" : "Self-Employed",
        color: "text-green-600",
        bgColor: "bg-green-50",
        points: [
          isTamil ? "குறைந்தபட்ச சராசரி ரொக்க லாபம் ஆண்டுக்கு ₹1,80,000." : "Minimum average cash profit is ₹1,80,000 a year.",
          isTamil ? "உற்பத்தியாளர், வியாபாரி, கடைக்காரர் அல்லது சேவை வழங்குநராக ஒரே தொழிலில் 3 வருடங்கள் தேவை." : "You need 3 years in the same business, as a manufacturer, trader, shopkeeper or service provider."
        ]
      },
      {
        id: 3,
        iconName: 'GlobeAltIcon',
        title: isTamil ? "என்ஆர்ஐகள் (NRI)" : "NRIs (Salaried only)",
        color: "text-purple-600",
        bgColor: "bg-purple-50",
        points: [
          isTamil ? "சம்பளம் பெறும் NRI-களுக்கு குறைந்தபட்ச வருமானம் மாதம் ₹80,000." : "Minimum income is ₹80,000 a month for salaried NRIs.",
          isTamil ? "NRI கடன்களுக்கு கூட்டு வருமானம் கணக்கிடப்படாது." : "Joint income does not count for NRI loans.",
          isTamil ? "இந்தியாவில் பவர் ஆஃப் அட்டர்னி (Power of Attorney) வைத்திருப்பவர் தேவை." : "You will also need a Power of Attorney holder in India."
        ]
      }
    ];

    const fetchCards = async () => {
      try {
        const [cardsResponse, settingsResponse] = await Promise.all([
          fetch('/api/admin/eligibility-scorecard/cards/active'),
          fetch('/api/admin/eligibility-scorecard/settings?page_type=eligibility_cards_section')
        ]);

        const cardsJson = await cardsResponse.json();
        const settingsJson = await settingsResponse.json();

        if (cardsJson.success && cardsJson.data.length > 0) {
          setCriteria(cardsJson.data.map(item => ({
            id: item.id,
            iconName: item.icon_name,
            title: isTamil ? item.title_ta : item.title_en,
            color: item.color_class,
            bgColor: item.bg_class,
            points: isTamil ? item.points_ta : item.points_en
          })));
        } else {
          setCriteria(defaultCriteria);
        }

        if (settingsJson.success && settingsJson.data) {
          setSectionSettings(prev => ({
            ...prev,
            // Apply fetched English title/subtitle if not in Tamil.
            // If in Tamil, keep the static fallback unless there is Tamil setting added in future.
            title: (!isTamil && settingsJson.data.section_title) ? settingsJson.data.section_title : prev.title,
            subtitle: (!isTamil && settingsJson.data.section_subtitle) ? settingsJson.data.section_subtitle : prev.subtitle
          }));
        }
      } catch (error) {
        console.error('Error fetching eligibility cards:', error);
        setCriteria(defaultCriteria);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, [isTamil]);

  return (
    <section className="py-20 bg-gradient-light relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, #283079 0%, transparent 50%)`
        }} />
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {sectionSettings.title}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed"
          >
            {sectionSettings.subtitle}
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10"
        >
          {criteria.map((item) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className="group"
            >
              <div className="card h-full bg-white rounded-3xl p-8 shadow-large hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col">
                <div className={`${item.bgColor} w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {(() => {
                    const Icon = IconMap[item.iconName] || BuildingOfficeIcon;
                    return <Icon className={`h-8 w-8 ${item.color}`} />;
                  })()}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  {item.title}
                </h3>

                <ul className="space-y-4 flex-grow">
                  {item.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 leading-relaxed font-medium">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 4. Static Products Section
// -------------------------------------------------------------
const StaticProductsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const isTamil = i18n.language === 'ta';

  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({
    section_title: "Our Loan Products",
    section_subtitle: "Complete home financing solutions for every need"
  });
  const [loading, setLoading] = useState(true);

  const iconMap = {
    HomeIcon,
    BuildingOffice2Icon,
    GlobeAsiaAustraliaIcon,
    WrenchScrewdriverIcon,
    PlusIcon,
    BanknotesIcon
  };

  const tamilProducts = [
    {
      id: 1,
      title: t('products.items.homeConstruction.title'),
      description: t('products.items.homeConstruction.description'),
      icon: HomeIcon,
      image: "/img/products/Mortage-loan-1768890278011-650563274.webp",
      href: "/home-construction-loan-india",
      gradient_from: "blue-500",
      gradient_to: "indigo-600",
      features: t('products.items.homeConstruction.features', { returnObjects: true }) || [
        "Stage-wise disbursement",
        "Competitive rates",
        "Flexible repayment"
      ]
    },
    {
      id: 2,
      title: t('products.items.plotConstruction.title'),
      description: t('products.items.plotConstruction.description'),
      icon: BuildingOffice2Icon,
      image: "/img/products/Resale-Loan-1770793045124-421642993.webp",
      href: "/composite-home-loan-plot-construction",
      gradient_from: "green-500",
      gradient_to: "emerald-600",
      features: t('products.items.plotConstruction.features', { returnObjects: true }) || [
        "Single loan solution",
        "Land + Construction",
        "End-to-end support"
      ]
    },
    {
      id: 3,
      title: t('products.items.nriLoan.title'),
      description: t('products.items.nriLoan.description'),
      icon: GlobeAsiaAustraliaIcon,
      image: "/img/products/NRI-1770793056590-681303133.webp",
      href: "/nri-home-loan-india",
      gradient_from: "purple-500",
      gradient_to: "violet-600",
      features: t('products.items.nriLoan.features', { returnObjects: true }) || [
        "NRI special rates",
        "Remote processing",
        "Overseas income"
      ]
    },
    {
      id: 4,
      title: t('products.items.renovation.title'),
      description: t('products.items.renovation.description'),
      icon: WrenchScrewdriverIcon,
      image: "/img/products/Renovation-Loan-1770793070223-521199614.webp",
      href: "/home-renovation-loan",
      gradient_from: "orange-500",
      gradient_to: "red-600",
      features: t('products.items.renovation.features', { returnObjects: true }) || [
        "Home improvements",
        "Quick approval",
        "Modern upgrades"
      ]
    },
    {
      id: 5,
      title: t('products.items.extension.title'),
      description: t('products.items.extension.description'),
      icon: PlusIcon,
      image: "/img/products/Extension-Loan-1770793079312-739487480.webp",
      href: "/home-extension-loan",
      gradient_from: "teal-500",
      gradient_to: "cyan-600",
      features: t('products.items.extension.features', { returnObjects: true }) || [
        "Space expansion",
        "Additional floors",
        "Value enhancement"
      ]
    },
    {
      id: 6,
      title: t('products.items.mortgage.title'),
      description: t('products.items.mortgage.description'),
      icon: BanknotesIcon,
      image: "/img/products/Mortage-loan-1770793089998-742427054.webp",
      href: "/loan-against-property-mortgage-loan",
      gradient_from: "pink-500",
      gradient_to: "rose-600",
      features: t('products.items.mortgage.features', { returnObjects: true }) || [
        "Loan against property",
        "Business funding",
        "Personal needs"
      ]
    }
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          axios.get('/api/admin/eligibility-scorecard/products/active'),
          axios.get('/api/admin/eligibility-scorecard/products/settings')
        ]);

        if (productsRes.data && productsRes.data.success) {
          const transformed = (productsRes.data.data || []).map(product => {
            let features = [];
            try {
              features = typeof product.features === 'string'
                ? JSON.parse(product.features)
                : product.features || [];
            } catch (e) {
              console.error('Error parsing features:', e);
            }

            return {
              id: product.id,
              title: product.title,
              description: product.description,
              icon: iconMap[product.icon_name] || HomeIcon,
              image: product.image_url ? (product.image_url.startsWith('http') ? product.image_url : (product.image_url.startsWith('/') ? product.image_url : `/${product.image_url}`)) : "/img/Scorecard/Aham.png",
              href: product.link || '#',
              gradient_from: product.gradient_from || 'blue-500',
              gradient_to: product.gradient_to || 'indigo-600',
              features: features
            };
          });
          setProducts(transformed);
        }

        if (settingsRes.data && settingsRes.data.success && settingsRes.data.data) {
          setSettings(settingsRes.data.data);
        }
      } catch (error) {
        console.error('Error fetching dynamic products for Scorecard page:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const displayProducts = isTamil ? tamilProducts : products;
  const displayTitle = isTamil ? t('products.title') : (settings?.section_title || "Our Loan Products");
  const displaySubtitle = isTamil ? t('products.subtitle') : (settings?.section_subtitle || "Complete home financing solutions for every need");

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section id="products" className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2 variants={cardVariants} className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {displayTitle}
          </motion.h2>
          <motion.p variants={cardVariants} className="text-xl text-gray-600 max-w-3xl mx-auto">
            {displaySubtitle}
          </motion.p>
        </motion.div>

        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayProducts.map((product) => (
            <a href={product.href} key={product.id} className="block">
              <motion.div
                variants={cardVariants}
                className="group cursor-pointer h-full"
                whileHover={{ y: -10 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="relative bg-white rounded-2xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden h-full flex flex-col">
                  <div className="relative h-48 overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center bg-no-repeat transform group-hover:scale-110 transition-transform duration-700"
                      style={{ backgroundImage: `url(${product.image})` }}
                      role="img"
                      aria-label={`${product.title} - AHAM Home Loan`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-r from-${product.gradient_from} to-${product.gradient_to} opacity-80`} />
                    <div className="absolute top-4 left-4 w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <product.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>

                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                        {product.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {product.description}
                      </p>

                      <div className="space-y-2 mb-6">
                        {(product.features || []).map((feature, idx) => (
                          <div key={idx} className="flex items-center space-x-2 text-sm">
                            <div className="w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                            <span className="text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <button className="flex items-center space-x-2 text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                      <span>{t('common.learnMore')}</span>
                      <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </motion.div>
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 5. Static EMI Calculator Section
// -------------------------------------------------------------
// const StaticEMICalculatorSection = () => {
//   const { t, i18n } = useTranslation();
//   const [ref, inView] = useInView({
//     triggerOnce: true,
//     threshold: 0.1,
//   });

//   const [formData, setFormData] = useState({
//     loanAmount: 5000000,
//     interestRate: 8.5,
//     tenureYears: 20,
//   });

//   const [result, setResult] = useState(null);
//   const [isCalculating, setIsCalculating] = useState(false);

//   const localCalculateEMI = (loanAmount, interestRate, tenureMonths) => {
//     const r = interestRate / (12 * 100);
//     const n = tenureMonths;
//     const emi = loanAmount * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
//     const totalAmount = emi * n;
//     const totalInterest = totalAmount - loanAmount;

//     return {
//       monthly_emi: emi,
//       total_amount: totalAmount,
//       total_interest: totalInterest,
//     };
//   };

//   const calculateEMI = useCallback(
//     debounce(async (amount, rate, years) => {
//       if (amount < 100000 || rate < 5 || years < 1) return;

//       setIsCalculating(true);
//       try {
//         const data = localCalculateEMI(amount, rate, years * 12);
//         setResult(data);
//       } catch (error) {
//         console.error('EMI calculation failed:', error);
//       } finally {
//         setIsCalculating(false);
//       }
//     }, 500),
//     []
//   );

//   useEffect(() => {
//     calculateEMI(formData.loanAmount, formData.interestRate, formData.tenureYears);
//   }, [formData, calculateEMI]);

//   const handleInputChange = (field, value) => {
//     setFormData((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const containerVariants = {
//     hidden: {},
//     visible: {
//       transition: {
//         staggerChildren: 0.2,
//       },
//     },
//   };

//   const itemVariants = {
//     hidden: { opacity: 0, y: 50 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       transition: {
//         type: 'spring',
//         stiffness: 100,
//         damping: 15,
//       },
//     },
//   };

//   const isTamil = i18n.language === 'ta';

//   const settings = {
//     section_title: "EMI Calculator",
//     section_subtitle: "Calculate your monthly EMI and total interest payable instantly",
//     cta_text: "Apply Now",
//     cta_link: "/contact-aham-housing-finance"
//   };

//   const cards = [
//     { id: 'default1', card_key: 'principal', title: 'Principal', icon_name: 'CurrencyRupeeIcon', color_class: 'blue' },
//     { id: 'default2', card_key: 'total_interest', title: 'Total Interest', icon_name: 'ClockIcon', color_class: 'orange' },
//     { id: 'default3', card_key: 'total_amount', title: 'Total Amount Payable', icon_name: 'ChartBarIcon', color_class: 'green' }
//   ];

//   return (
//     <section id="emi-calculator" className="py-20 bg-gradient-light relative overflow-hidden">
//       <div className="absolute inset-0 opacity-5">
//         <div
//           className="absolute inset-0"
//           style={{
//             backgroundImage: `radial-gradient(circle at 20% 80%, #283079 0%, transparent 50%),
//                            radial-gradient(circle at 80% 20%, #283079 0%, transparent 50%),
//                            radial-gradient(circle at 40% 40%, #4f46e5 0%, transparent 50%)`,
//           }}
//         />
//       </div>

//       <div className="container mx-auto px-4 relative">
//         <motion.div
//           ref={ref}
//           initial="hidden"
//           animate={inView ? 'visible' : 'hidden'}
//           variants={containerVariants}
//           className="text-center mb-12"
//         >
//           <motion.div variants={itemVariants} className="flex justify-center mb-6">
//             <div className="bg-primary-100 p-4 rounded-2xl">
//               <CalculatorIcon className="h-12 w-12 text-primary-600" />
//             </div>
//           </motion.div>

//           <motion.h2
//             variants={itemVariants}
//             className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
//           >
//             {isTamil ? t('emiCalculator.title') : settings.section_title}
//           </motion.h2>

//           <motion.p
//             variants={itemVariants}
//             className="text-xl text-gray-600 max-w-2xl mx-auto"
//           >
//             {isTamil ? t('emiCalculator.subtitle') : settings.section_subtitle}
//           </motion.p>
//         </motion.div>

//         <motion.div
//           variants={containerVariants}
//           className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto"
//         >
//           <motion.div variants={itemVariants} className="bg-white rounded-3xl shadow-large p-8 lg:p-12">
//             <div className="space-y-8">
//               <div>
//                 <label className="block text-sm font-semibold text-gray-700 mb-3">
//                   {t('emiCalculator.loanAmount')}
//                 </label>
//                 <div className="space-y-4">
//                   <input
//                     type="range"
//                     min="100000"
//                     max="50000000"
//                     step="100000"
//                     value={formData.loanAmount}
//                     onChange={(e) => handleInputChange('loanAmount', parseInt(e.target.value))}
//                     className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
//                   />
//                   <div className="flex justify-between text-sm text-gray-500">
//                     {isTamil ? (
//                       <>
//                         <span>₹1 லட்சம்</span>
//                         <span className="font-semibold text-primary-600 text-lg">
//                           {formatCurrency(formData.loanAmount)}
//                         </span>
//                         <span>₹5 கோடி</span>
//                       </>
//                     ) : (
//                       <>
//                         <span>₹1L</span>
//                         <span className="font-semibold text-primary-600 text-lg">
//                           {formatCurrency(formData.loanAmount)}
//                         </span>
//                         <span>₹5Cr</span>
//                       </>
//                     )}
//                   </div>
//                 </div>
//               </div>

//               <div>
//                 <label className="block text-sm font-semibold text-gray-700 mb-3">
//                   {t('emiCalculator.interestRate')}
//                 </label>
//                 <div className="space-y-4">
//                   <input
//                     type="range"
//                     min="5"
//                     max="20"
//                     step="0.1"
//                     value={formData.interestRate}
//                     onChange={(e) => handleInputChange('interestRate', parseFloat(e.target.value))}
//                     className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
//                   />
//                   <div className="flex justify-between text-sm text-gray-500">
//                     <span>5%</span>
//                     <span className="font-semibold text-primary-600 text-lg">
//                       {formData.interestRate}% {isTamil ? 'வருடத்திற்கு' : 'p.a.'}
//                     </span>
//                     <span>20%</span>
//                   </div>
//                 </div>
//               </div>

//               <div>
//                 <label className="block text-sm font-semibold text-gray-700 mb-3">
//                   {t('emiCalculator.tenure')}
//                 </label>
//                 <div className="space-y-4">
//                   <input
//                     type="range"
//                     min="1"
//                     max="30"
//                     step="1"
//                     value={formData.tenureYears}
//                     onChange={(e) => handleInputChange('tenureYears', parseInt(e.target.value))}
//                     className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
//                   />
//                   <div className="flex justify-between text-sm text-gray-500">
//                     <span>1 {isTamil ? 'ஆண்டு' : 'Year'}</span>
//                     <span className="font-semibold text-primary-600 text-lg">
//                       {formData.tenureYears} {isTamil ? 'ஆண்டுகள்' : 'Years'}
//                     </span>
//                     <span>30 {isTamil ? 'ஆண்டுகள்' : 'Years'}</span>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </motion.div>

//           <motion.div variants={itemVariants} className="space-y-8">
//             {result && !isCalculating ? (
//               <>
//                 <div className="flex flex-col xl:flex-row gap-6">
//                   <div className="flex-1 bg-white rounded-3xl shadow-soft border border-gray-100 p-8 relative overflow-hidden flex flex-col items-center justify-center min-h-[320px] text-center">
//                     <div className="relative z-10 flex flex-col items-center">
//                       <div className="flex flex-col items-center mb-4">
//                         <CurrencyRupeeIcon className="h-10 w-10 text-primary-600 mb-2" />
//                         <span className="text-gray-600 text-lg uppercase tracking-wider font-semibold">
//                           {t('emiCalculator.monthlyEmi')}
//                         </span>
//                       </div>
//                       <div className="text-4xl lg:text-5xl font-extrabold mb-2 text-gray-900">
//                         {formatCurrency(result.monthly_emi)}
//                       </div>
//                       <div className="text-gray-500 text-sm font-medium">
//                         {isTamil ? t('emiCalculator.monthlyPaymentDetail') : 'Monthly Payment Detail'}
//                       </div>
//                     </div>
//                   </div>

//                   <div className="flex-1 bg-white rounded-3xl shadow-soft p-6 flex flex-col items-center justify-center min-h-[320px]">
//                     <h3 className="text-sm font-bold text-gray-500 mb-4 text-center uppercase tracking-wider">
//                       {isTamil ? t('emiCalculator.breakupTitle') : 'Break-up of Total Payment'}
//                     </h3>
//                     <div className="h-64 w-full">
//                       <ResponsiveContainer width="100%" height="100%">
//                         <PieChart>
//                           <Pie
//                             data={[
//                               { name: isTamil ? t('emiCalculator.principalLabel') : 'Principal Loan Amount', value: formData.loanAmount },
//                               { name: isTamil ? t('emiCalculator.interestLabel') : 'Total Interest', value: result.total_interest }
//                             ]}
//                             cx="50%"
//                             cy="50%"
//                             outerRadius={80}
//                             fill="#8884d8"
//                             dataKey="value"
//                             label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
//                               const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
//                               const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
//                               const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
//                               return (
//                                 <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-xs font-bold">
//                                   {`${(percent * 100).toFixed(0)}%`}
//                                 </text>
//                               );
//                             }}
//                             labelLine={false}
//                           >
//                             <Cell fill="#283079" />
//                             <Cell fill="#ed2636" />
//                           </Pie>
//                           <Tooltip formatter={(value) => formatCurrency(value)} />
//                           <Legend verticalAlign="bottom" height={36} iconType="square" />
//                         </PieChart>
//                       </ResponsiveContainer>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//                   {cards.map((card) => {
//                     const IconComponent = {
//                       CurrencyRupeeIcon,
//                       ClockIcon,
//                       ChartBarIcon
//                     }[card.icon_name] || CurrencyRupeeIcon;

//                     const colorMap = {
//                       blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
//                       orange: { bg: 'bg-orange-100', text: 'text-orange-600' },
//                       green: { bg: 'bg-green-100', text: 'text-green-600' }
//                     };

//                     const colors = colorMap[card.color_class] || colorMap.blue;

//                     const valueMap = {
//                       principal: formData.loanAmount,
//                       total_interest: result.total_interest,
//                       total_amount: result.total_amount
//                     };

//                     const localizedTitleMap = {
//                       principal: t('emiCalculator.principalLabel'),
//                       total_interest: t('emiCalculator.interestLabel'),
//                       total_amount: t('emiCalculator.totalAmount')
//                     };

//                     const displayTitle = isTamil ? localizedTitleMap[card.card_key] : card.title;

//                     return (
//                       <div key={card.id} className="bg-white rounded-2xl shadow-soft p-6 flex flex-col items-center text-center">
//                         <div className="flex flex-col items-center mb-3">
//                           <div className={`${colors.bg} p-3 rounded-xl mb-2`}>
//                             <IconComponent className={`h-6 w-6 ${colors.text}`} />
//                           </div>
//                           <span className="text-gray-600 font-semibold text-sm uppercase tracking-wider">
//                             {displayTitle}
//                           </span>
//                         </div>
//                         <div className="text-2xl font-extrabold text-gray-900">
//                           {formatCurrency(valueMap[card.card_key] || 0)}
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>

//                 <div className="flex justify-center mt-6">
//                   <button
//                     onClick={() => window.location.href = settings.cta_link}
//                     className="bg-[#ed2636] text-white px-6 py-2.5 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl flex items-center justify-center gap-3 w-[60%] sm:w-auto mx-auto hover:bg-red-700"
//                   >
//                     <span>{isTamil ? t('common.applyNow') : settings.cta_text}</span>
//                   </button>
//                 </div>
//               </>
//             ) : (
//               <div className="bg-white rounded-3xl shadow-large p-12 text-center">
//                 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
//                 <p className="text-gray-600">{isTamil ? 'கணக்கிடுகிறது...' : 'Calculating EMI...'}</p>
//               </div>
//             )}
//           </motion.div>
//         </motion.div>
//       </div>

//       <style jsx>{`
//         .slider::-webkit-slider-thumb {
//           appearance: none;
//           height: 24px;
//           width: 24px;
//           border-radius: 50%;
//           background: #283079;
//           cursor: pointer;
//           box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
//           border: 3px solid white;
//         }

//         .slider::-moz-range-thumb {
//           height: 24px;
//           width: 24px;
//           border-radius: 50%;
//           background: #283079;
//           cursor: pointer;
//           border: 3px solid white;
//           box-shadow: 0 4px 12px rgba(40, 48, 121, 0.4);
//         }
//       `}</style>
//     </section>
//   );
// };

// -------------------------------------------------------------
// 6. Custom Speedometer Component for Scorecard
// -------------------------------------------------------------
const CustomSpeedometer = ({ score, maxScore }) => {
  const { t, i18n } = useTranslation();
  const isTamil = i18n.language === 'ta';

  const radius = 120;
  const stroke = 30;
  const centerX = 150;
  const centerY = 150;
  const percent = Math.min(Math.max(score / maxScore, 0), 1);

  const segmentColors = ['#F56565', '#ED8936', '#48BB78', '#38B2AC'];
  const labels = [
    isTamil ? t('eligibilityScorecard.poor') : "POOR",
    isTamil ? t('eligibilityScorecard.average') : "AVERAGE",
    isTamil ? t('eligibilityScorecard.good') : "GOOD",
    isTamil ? t('eligibilityScorecard.excellent') : "EXCELLENT"
  ];

  const polarToCartesian = (cx, cy, r, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 180) * Math.PI / 180.0;
    return {
      x: cx + (r * Math.cos(angleInRadians)),
      y: cy + (r * Math.sin(angleInRadians))
    };
  };

  const describeArc = (x, y, r, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, r, endAngle);
    const end = polarToCartesian(x, y, r, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return [
      "M", start.x, start.y,
      "A", r, r, 0, largeArcFlag, 0, end.x, end.y
    ].join(" ");
  };

  const segments = segmentColors.map((color, i) => {
    const startAngle = i * 45;
    const endAngle = (i + 1) * 45;
    const path = describeArc(centerX, centerY, radius, startAngle, endAngle);

    return (
      <path
        key={i}
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
      />
    );
  });

  const segmentLabels = labels.map((label, i) => {
    const angle = (i * 45) + 22.5;
    const pos = polarToCartesian(centerX, centerY, radius - 35, angle);
    return (
      <text
        key={`seg-label-${i}`}
        x={pos.x}
        y={pos.y}
        fill="white"
        fontSize="10"
        fontWeight="bold"
        textAnchor="middle"
        transform={`rotate(${angle - 90}, ${pos.x}, ${pos.y})`}
      >
        {label}
      </text>
    )
  });

  const ticks = [];
  for (let i = 0; i <= 100; i += 10) {
    const angle = (i / 100) * 180;
    const tickStart = polarToCartesian(centerX, centerY, radius - stroke / 2 - 5, angle);
    const tickEnd = polarToCartesian(centerX, centerY, radius - stroke / 2 + 5, angle);
    const textPos = polarToCartesian(centerX, centerY, radius - stroke - 15, angle);

    ticks.push(
      <g key={i}>
        <line
          x1={tickStart.x}
          y1={tickStart.y}
          x2={tickEnd.x}
          y2={tickEnd.y}
          stroke="#ffffff"
          strokeWidth="1"
        />
        <text
          x={textPos.x}
          y={textPos.y}
          fill="#ffffff"
          fontSize="10"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {i}
        </text>
      </g>
    );
  }

  const needleAngle = percent * 180;

  return (
    <div style={{ position: 'relative', width: '100%', height: 'auto', maxWidth: '400px', margin: '0 auto' }}>
      <svg viewBox={`0 0 ${centerX * 2} ${centerY + 20}`} style={{ width: '100%', height: 'auto' }}>
        {segments}
        {segmentLabels}
        {ticks}
        <g style={{ transition: 'transform 1s ease-out', transformOrigin: `${centerX}px ${centerY}px`, transform: `rotate(${needleAngle}deg)` }}>
          <circle cx={centerX} cy={centerY} r="8" fill="#333" />
          <path d={`M ${centerX} ${centerY - 5} L ${centerX - (radius - 10)} ${centerY} L ${centerX} ${centerY + 5} Z`} fill="#333" />
        </g>
      </svg>
    </div>
  );
};

// -------------------------------------------------------------
// 7. Static Eligibility Scorecard Section
// -------------------------------------------------------------
const StaticEligibilityScorecardSection = () => {
  const { t, i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [score, setScore] = useState(0);
  const [result, setResult] = useState(null);
  const [showCreditScoreModal, setShowCreditScoreModal] = useState(false);
  const [showCreditScoreModal2, setShowCreditScoreModal2] = useState(false);

  const isTamil = i18n.language === 'ta';

  const settings = {
    section_title: "How Home Loan Eligibility Is Calculated using Aham’s Exclusive Scorecard Method",
    section_subtitle: "It is simple… Just share few details about you & your property. Your search for your dream home ends here."
  };

  const questions = [
    // {
    //   id: "question_1",
    //   question: "Select your loan type",
    //   options: [
    //     { label: "Purchase of new property", value: "Purchase of new property", points: 5 },
    //     { label: "Purchase of an under-construction property", value: "Purchase of an under-construction property", points: -5 },
    //     { label: "Purchase of an existing property", value: "Purchase of an existing property", points: 5 },
    //     { label: "Self-construction on an existing plot", value: "Self-construction on an existing plot", points: 4 },
    //     { label: "Purchase of a Plot & Construction (Composite loan)", value: "Purchase of a Plot & Construction (Composite loan)", points: -2.5 },
    //     { label: "Repair", value: "Repair", points: 2.5 },
    //     { label: "Renovation/Extension ", value: "Renovation/Extension ", points: 2 },
    //     { label: "Take over", value: "Take over", points: -2.5 },
    //     { label: "Loan against property (LAP)", value: "Loan against property (LAP)", points: -3.5 },
    //     { label: "Loan against property (LAP) - Rented/Vacent/Letout", value: "Loan against property (LAP) - Rented/Vacent/Letout", points: -50 }
    //   ]
    // },
    {
      id: "question_2",
      question: "How will you use the property?",
      options: [
        { label: "Self-occupied", value: "Self-occupied", points: 10 },
        { label: "Partly self-occupied & commercial", value: "Partly self-occupied & commercial", points: 5 },
        { label: "Rented", value: "Rented", points: 2.5 },
        { label: "Unoccupied/Vacant property", value: "Unoccupied/Vacant property", points: -10 }
      ]
    },
    {
      id: "question_3",
      question: "Does your property have plan approval?",
      options: [
        { label: "Available", value: "Available", points: 5 },
        { label: "Applied for", value: "Applied for", points: 3.5 },
        { label: "Deemed approval/Low demolition risk", value: "Deemed approval/Low demolition risk", points: -2 },
        { label: "High demolition risk", value: "High demolition risk", points: -50 }
      ]
    },
    {
      id: "question_4",
      question: "Enter your required loan amount (in INR)",
      type: "text",
      pointsLogic: () => 0
    },
    {
      id: "question_5",
      question: "Enter the estimated market value of your property (in INR)",
      type: "text",
      pointsLogic: () => 0
    },
    {
      id: "question_6",
      question: "The approximate distance of your property from our nearest branch ",
      options: [
        { label: "Within city/Town limits/5 KM", value: "Within city/Town limits/5 KM", points: 15 },
        { label: "Within 15 KMs from City/Town", value: "Within 15 KMs from City/Town", points: 11.25 },
        { label: "Above 15 KMs from City/Town", value: "Above 15 KMs from City/Town", points: -3.75 },
        { label: "Above 50 KMs from Branch/Village/Rural", value: "Above 50 KMs from Branch/Village/Rural", points: -15 }
      ]
    },
    {
      id: "question_7",
      question: "What is your CIBIL score?",
      options: [
        { label: "Above 700", value: "Above 700", points: 5 },
        { label: "Between 600 to 700", value: "Between 600 to 700", points: 2.5 },
        { label: "Less than 600", value: "Less than 600", points: -50 }
      ]
    },
    {
      id: "question_8",
      question: "Do you currently pay any EMIs (like personal, vehicle, or housing loans) with a good repayment history?",
      options: [
        { label: "Good repayment track record over 24 months", value: "Good repayment track record over 24 months", points: 10 },
        { label: "Good repayment track record over 12 months and below 24 months", value: "Good repayment track record over 12 months and below 24 months", points: 7.5 },
        { label: "Good repayment track record below 12 months", value: "Good repayment track record below 12 months", points: 5 },
        { label: "NIL Repayment track/ Repayment track record prior to 5 years", value: "NIL Repayment track/ Repayment track record prior to 5 years", points: 0 }
      ]
    },
    {
      id: "question_9",
      question: "What is your average monthly income (in INR)?",
      options: [
        { label: "More than Or Equal to 75000", value: "More than Or Equal to 75000", points: 15 },
        { label: "More than 50000 and less than 75000", value: "More than 50000 and less than 75000", points: 10.5 },
        { label: "More than 25000 and less than 50000", value: "More than 25000 and less than 50000", points: 9 },
        { label: "Less than Or Equal to 25000", value: "Less than Or Equal to 25000", points: 6 }
      ]
    },
    {
      id: "question_10",
      question: "Approximately what percentage of your income goes toward monthly expenses and commitments?",
      options: [
        { label: "Expenses/Commitments less than 40% of total income", value: "Expenses/Commitments less than 40% of total income", points: 15 },
        { label: "Expenses/Commitments between 41% to 50% of total income", value: "Expenses/Commitments between 41% to 50% of total income", points: 10.5 },
        { label: "Expenses/Commitments between 51% to 60% of total income", value: "Expenses/Commitments between 51% to 60% of total income", points: 9 },
        { label: "Expenses/Commitments more than 60% of total income", value: "Expenses/Commitments more than 60% of total income", points: 6 }
      ]
    },
    {
      id: "question_11",
      question: "Who contributes to your family's income?",
      options: [
        { label: "Both Husband and Wife ", value: "Both Husband and Wife ", points: 5 },
        { label: "Husband or Wife", value: "Husband or Wife", points: 3.25 },
        { label: "Both Husband and Wife along with other family members(Parent or unmarried siblings)", value: "Both Husband and Wife along with other family members(Parent or unmarried siblings)", points: -2.5 }
      ]
    },
    {
      id: "question_12",
      question: "What is your age group?",
      options: [
        { label: "Less than 30  ", value: "Less than 30 ", points: 3 },
        { label: "More than 30 but less than 40", value: "More than 30 but less than 40", points: 2.1 },
        { label: "More than 40 but less than 50", value: "More than 40 but less than 50", points: 1.8 },
        { label: "More than 50", value: "More than 50", points: 0.9 }
      ]
    },
    {
      id: "question_13",
      question: "How many years of experience do you have in your current job or business?",
      options: [
        { label: "More than 2 years", value: "More than 2 years", points: 2 },
        { label: "Less than 2 years", value: "Less than 2 years", points: 1 },
        { label: "No evidence", value: "No evidence", points: -0.2 }
      ]
    },
    {
      id: "question_14",
      question: "Please select your employment type or income source",
      options: [
        { label: "Salaried-PSU/Govt / Public limited", value: "Salaried-PSU/Govt / Public limited", points: 10 },
        { label: "Business-As per IT returns", value: "Business-As per IT returns", points: 10 },
        { label: "Assessed Income - PD Based", value: "Assessed Income - PD Based", points: 7.5 },
        { label: "Salaried-Private company", value: "Salaried-Private company", points: 7.5 },
        { label: "Cash Salary", value: "Cash Salary", points: 1 },
        { label: "Assessed Income with no proof", value: "Assessed Income with no proof", points: -5 }
      ]
    }
  ];

  const sectionTitles = React.useMemo(() => {
    const sections = [];
    let currentCategory = null;
    let startIndex = 0;

    questions.forEach((q, index) => {
      let category;
      if (index <= 7) category = isTamil ? "சொத்து மதிப்பீடு" : "Property Assessment";
      else if (index <= 10) category = isTamil ? "வருமான மதிப்பீடு" : "Income Assessment";
      else category = isTamil ? "மக்கள்தொகை மதிப்பீடு" : "Demographic Assessment";

      if (currentCategory !== category) {
        if (currentCategory !== null) {
          sections.push({ range: [startIndex, index - 1], title: currentCategory });
        }
        currentCategory = category;
        startIndex = index;
      }
    });

    if (currentCategory !== null) {
      sections.push({ range: [startIndex, questions.length - 1], title: currentCategory });
    }

    return sections;
  }, [isTamil]);

  const currentSection =
    sectionTitles.find((s) => currentStep >= s.range[0] && currentStep <= s.range[1])?.title || "";

  const maxScore = questions.reduce((sum, q) => {
    if (q.options && q.options.length > 0) {
      return sum + Math.max(...q.options.map((o) => o.points));
    }
    return sum;
  }, 0);

  const handleSelect = (id, option) => {
    setResponses((prev) => ({ ...prev, [id]: option }));
    const otherScores = Object.entries(responses)
      .filter(([key]) => key !== id)
      .reduce((sum, [, opt]) => sum + (opt?.points || 0), 0);
    setScore(otherScores + option.points);
  };

  const handleManualInput = (id, value, logicFn) => {
    const points = logicFn(value);
    const updated = { value, points };
    setResponses((prev) => ({ ...prev, [id]: updated }));
    const total = Object.entries({ ...responses, [id]: updated }).reduce(
      (sum, [, opt]) => sum + (opt?.points || 0),
      0
    );
    setScore(total);
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) setCurrentStep(currentStep + 1);
    else setResult(true);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  return (
    <section id="scorecard" className="py-20 bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900">
      <div className="container mx-auto px-4">
        <motion.div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-white mb-4">
            {isTamil ? t('eligibilityScorecard.title') : settings.section_title}
          </h2>
          <p className="text-blue-100 text-lg">
            {isTamil ? t('eligibilityScorecard.subtitle') : settings.section_subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
          <div className="bg-white shadow-lg rounded-3xl p-8 h-full flex flex-col justify-center animate-fade-in">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              {isTamil ? t('eligibilityScorecard.scorecard') : 'Scorecard'}
            </h3>

            <div className="flex flex-col items-center mb-10 w-full">
              <CustomSpeedometer score={score} maxScore={maxScore} />

              <div style={{ marginTop: 12, width: '100%', maxWidth: '400px' }}>
                <div className="flex items-center justify-between w-full text-[10px] text-gray-700 font-bold px-2 uppercase tracking-tighter">
                  <div className="flex-1 flex flex-col items-center">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#FF4E42', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.poor') : 'BELOW'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center px-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#FFA500', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.average') : 'AVERAGE'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center px-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#48BB78', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.good') : 'GOOD'}</div>
                  </div>
                  <div className="flex-1 flex flex-col items-center pl-2">
                    <div className="w-full h-3 rounded-t-md" style={{ background: '#38B2AC', opacity: 0.9 }} />
                    <div className="mt-2">{isTamil ? t('eligibilityScorecard.excellent') : 'EXCELLENT'}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                <span>
                  {isTamil ? t('eligibilityScorecard.question') : 'Question'} {currentStep + 1} {isTamil ? t('eligibilityScorecard.of') : 'of'} {questions.length}
                </span>
                <span className="text-indigo-700 font-bold">
                  {Math.round(((currentStep + 1) / questions.length) * 100)}% {isTamil ? t('eligibilityScorecard.complete') : 'Complete'}
                </span>
              </div>

              <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner">
                <div
                  className="h-2.5 rounded-full bg-indigo-700 transition-all duration-500 ease-out"
                  style={{
                    width: `${((currentStep + 1) / questions.length) * 100}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="h-full flex flex-col">
            {!result ? (
              <div className="bg-white shadow-lg rounded-3xl p-8 h-full flex flex-col justify-center relative min-h-[400px]">
                <div className="absolute top-6 left-6">
                  <span className="text-gray-400 font-bold text-xs uppercase tracking-widest">
                    {currentStep + 1} / {questions.length}
                  </span>
                </div>

                {currentSection && (
                  <div className="absolute top-6 right-6">
                    <span className="inline-block bg-indigo-100 text-indigo-800 px-4 py-2 rounded-full text-[10px] font-black uppercase shadow-sm tracking-widest">
                      {currentSection}
                    </span>
                  </div>
                )}

                <h3 className="text-2xl font-bold text-gray-900 mb-10 text-center mt-8 leading-tight">
                  {questions[currentStep].question}
                </h3>

                {questions[currentStep].type === 'text' || !questions[currentStep].options || questions[currentStep].options.length === 0 ? (
                  <div className="mt-6" key={questions[currentStep].id}>
                    <input
                      type="text"
                      placeholder={isTamil ? "உங்கள் பதிலை இங்கே தட்டச்சு செய்யவும்..." : "Type your answer here..."}
                      className="w-full border-2 border-gray-100 rounded-2xl p-4 focus:ring-4 focus:ring-primary-100 focus:border-primary-500 transition-all text-lg font-medium"
                      value={responses[questions[currentStep].id]?.value || ''}
                      onChange={(e) =>
                        handleManualInput(
                          questions[currentStep].id,
                          e.target.value,
                          questions[currentStep].pointsLogic
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {questions[currentStep].options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSelect(questions[currentStep].id, option)}
                        className={`p-5 border-2 rounded-2xl text-left transition-all duration-300 font-bold text-sm leading-snug shadow-sm ${responses[questions[currentStep].id]?.value === option.value
                          ? 'border-primary-600 bg-primary-50 text-primary-700 transform scale-[1.02] shadow-md'
                          : 'border-gray-50 hover:border-primary-200 hover:bg-gray-50'
                          }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex justify-between items-center mt-12">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 0}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-black uppercase text-xs tracking-widest transition-all ${currentStep === 0
                      ? 'text-gray-200 cursor-not-allowed'
                      : 'text-gray-500 hover:text-primary-600 hover:bg-primary-50'
                      }`}
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    <span>{isTamil ? t('eligibilityScorecard.previous') : 'Previous'}</span>
                  </button>

                  <button
                    onClick={nextStep}
                    disabled={!responses[questions[currentStep].id]}
                    className={`flex items-center space-x-3 px-10 py-4 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-lg ${!responses[questions[currentStep].id]
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-[#ed2636] hover:bg-primary-700 text-white transform hover:scale-105 active:scale-95'
                      }`}
                  >
                    <span>{currentStep === questions.length - 1 ? (isTamil ? t('eligibilityScorecard.getResult') : 'Get Result') : (isTamil ? t('eligibilityScorecard.next') : 'Next')}</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl p-10 text-center shadow-2xl border-4 border-primary-50 h-full flex flex-col justify-center">
                <h2 className="text-3xl font-black text-gray-900 mb-6 uppercase tracking-tight">
                  {isTamil ? t('eligibilityScorecard.assessmentComplete') : 'Assessment Complete!'}
                </h2>

                <div className="flex justify-center mb-10">
                  <button
                    onClick={() => setShowCreditScoreModal2(true)}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-xs tracking-widest px-8 py-5 rounded-2xl shadow-xl transition-all hover:scale-105 active:scale-95"
                  >
                    {isTamil ? t('eligibilityScorecard.checkCreditScore') : 'Check Credit Score'}
                  </button>
                </div>

                <p className="text-xl text-gray-600 font-medium leading-relaxed">
                  {isTamil ? 'உங்கள் இடுபொருள்களின் அடிப்படையில் உங்கள் தகுதி மதிப்பெண் கணக்கிடப்படுகிறது.' : 'Your eligibility score is based on your inputs.'}
                </p>
              </div>
            )}
          </div>
        </div>

        <CreditScoreModal
          isOpen={showCreditScoreModal}
          onClose={() => setShowCreditScoreModal(false)}
        />
        <CreditScoreModal2
          isOpen={showCreditScoreModal2}
          onClose={() => setShowCreditScoreModal2(false)}
          answers={responses}
          score={score}
        />
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 8. Static Testimonials Section
// -------------------------------------------------------------
const StaticTestimonialsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [currentIndex, setCurrentIndex] = useState(0);

  const isTamil = i18n.language === 'ta';

  const settings = {
    section_title: "What Our Customers Say",
    section_subtitle: "Real experiences from satisfied homeowners"
  };

  const englishTestimonials = [
    {
      id: 1,
      name: "Rajesh Kumar R",
      location: "Mumbai, Maharashtra",
      rating: 5,
      text: "AHAM made my dream of owning a home come true! Their no-income-proof process was exactly what I needed as a freelance consultant. The team was supportive throughout.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "Home Construction Loan"
    },
    {
      id: 2,
      name: "Priya Sharma",
      location: "Bangalore, Karnataka",
      rating: 5,
      text: "Excellent service and quick processing! I got my home loan approved in just 10 days. The interest rates were competitive and the staff was very professional.",
      image_url: "/img/testimonial/16.png",
      loan_type: "Plot + Construction Loan"
    },
    {
      id: 3,
      name: "Amit Patel",
      location: "Ahmedabad, Gujarat",
      rating: 5,
      text: "As an NRI, I was worried about the documentation process. But AHAM team handled everything smoothly. I could complete most formalities online.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "NRI Home Loan"
    },
    {
      id: 4,
      name: "Sunita Reddy",
      location: "Hyderabad, Telangana",
      rating: 5,
      text: "I renovated my entire house with their renovation loan. The stage-wise disbursement helped me manage the project better. Great experience overall!",
      image_url: "/img/testimonial/16.png",
      loan_type: "Home Renovation Loan"
    },
    {
      id: 5,
      name: "Vikram Singh",
      location: "Jaipur, Rajasthan",
      rating: 5,
      text: "Transparent process, no hidden charges, and excellent customer support. AHAM is definitely the best choice for home loans in India.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "Home Extension Loan"
    }
  ];

  const tamilTestimonials = [
    {
      id: "ta_0",
      name: "ராஜேஷ் குமார் ஆர்",
      location: "மும்பை, மகாராஷ்டிரா",
      rating: 5,
      text: "AHAM என் சொந்த வீட்டு கனவை நனவாக்கியது! நான் ஒரு சுயதொழில் ஆலோசகராக (Freelance Consultant) இருப்பதால், வருமான ஆவணங்கள் இல்லாத செயல்முறை எனக்கு மிகவும் உதவியாக இருந்தது. முழு செயல்முறையிலும் அணி எனக்கு உறுதுணையாக இருந்தது.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "வீட்டு கட்டுமானக் கடன்"
    },
    {
      id: "ta_1",
      name: "பிரியா ஷர்மா",
      location: "பெங்களூர், கர்நாடகா",
      rating: 5,
      text: "சிறந்த சேவை மற்றும் விரைவான செயல்முறை! என் வீட்டு கடன் வெறும் 10 நாட்களில் ஒப்புதல் பெற்றது. வட்டி விகிதங்கள் போட்டித்திறனாக இருந்தன, மேலும் பணியாளர்கள் மிகவும் தொழில்முறை அணுகுமுறையுடன் செயல்பட்டனர்.",
      image_url: "/img/testimonial/16.png",
      loan_type: "ப்ளாட் + கட்டுமானக் கடன்"
    },
    {
      id: "ta_2",
      name: "அமித் படேல்",
      location: "அகமதாபாத், குஜராத்",
      rating: 5,
      text: "ஒரு NRI ஆக இருந்ததால், ஆவண செயல்முறை குறித்து எனக்கு கவலை இருந்தது. ஆனால் AHAM அணி அனைத்தையும் மிகவும் சீராகவும் எளிதாகவும் மேற்கொண்டது. பெரும்பாலான செயல்முறைகளை நான் ஆன்லைனில் முடிக்க முடிந்தது.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "NRI வீட்டு கடன்"
    },
    {
      id: "ta_3",
      name: "சுனிதா ரெட்டி",
      location: "ஹைதராபாத், தெலங்கானா",
      rating: 5,
      text: "அவர்களின் புதுப்பிப்பு கடன் உதவியுடன் என் வீட்டை முழுவதும் புதுப்பித்தேன். கட்டுமான முன்னேற்றத்திற்கு ஏற்ப வழங்கப்பட்ட படிப்படையான நிதி, திட்டத்தை சிறப்பாக நிர்வகிக்க உதவியது. மொத்தத்தில் மிகச் சிறந்த அனுபவம்!",
      image_url: "/img/testimonial/16.png",
      loan_type: "வீட்டு புதுப்பிப்பு கடன்"
    },
    {
      id: "ta_4",
      name: "விக்ரம் சிங்",
      location: "ஜெய்ப்பூர், ராஜஸ்தான்",
      rating: 5,
      text: "வெளிப்படையான செயல்முறை, மறைமுக கட்டணங்கள் எதுவும் இல்லை, மேலும் சிறந்த வாடிக்கையாளர் ஆதரவு. இந்தியாவில் வீட்டு கடன்களுக்கு AHAM நிச்சயமாக சிறந்த தேர்வு.",
      image_url: "/img/testimonial/9.webp",
      loan_type: "வீட்டு விரிவாக்கக் கடன்"
    }
  ];

  const testimonials = isTamil ? tamilTestimonials : englishTestimonials;

  useEffect(() => {
    if (currentIndex >= testimonials.length && testimonials.length > 0) {
      setCurrentIndex(0);
    }

    const timer = setInterval(() => {
      if (testimonials.length > 0) {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [testimonials.length, currentIndex]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  const displayTitle = isTamil ? t('testimonials.title') : settings.section_title;
  const displaySubtitle = isTamil ? t('testimonials.subtitle') : settings.section_subtitle;

  return (
    <section className="py-20 bg-gradient-light relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-600 rounded-full mix-blend-multiply filter blur-xl animate-float"></div>
        <div className="absolute top-3/4 right-1/4 w-64 h-64 bg-purple-600 rounded-full mix-blend-multiply filter blur-xl animate-float" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="container mx-auto px-4 relative">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {displayTitle}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {displaySubtitle}
          </motion.p>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="max-w-6xl mx-auto"
        >
          {testimonials.length > 0 && (
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-large p-8 lg:p-12 relative overflow-hidden">
                <div className="absolute top-8 left-8 text-primary-200 text-6xl font-serif">
                  "
                </div>

                <div className="relative z-10">
                  <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
                    <div className="flex-shrink-0 text-center lg:text-left">
                      <div className="w-24 h-24 mx-auto lg:mx-0 mb-4 relative">
                        <img
                          src={testimonials[currentIndex]?.image_url}
                          alt={`Testimonial from ${testimonials[currentIndex]?.name} - AHAM Housing`}
                          className="w-full h-full rounded-full object-cover"
                        />
                        <div className="absolute -bottom-2 -right-2 bg-primary-600 rounded-full p-2">
                          <StarIconSolid className="h-4 w-4 text-white" />
                        </div>
                      </div>

                      <h4 className="text-xl font-bold text-gray-900 mb-1">
                        {testimonials[currentIndex]?.name}
                      </h4>

                      <p className="text-gray-600 text-sm mb-3">
                        {testimonials[currentIndex]?.location}
                      </p>

                      <div className="inline-block bg-primary-50 text-primary-600 px-3 py-1 rounded-full text-xs font-medium">
                        {testimonials[currentIndex]?.loan_type}
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-center lg:justify-start space-x-1 mb-4">
                        {[...Array(testimonials[currentIndex]?.rating || 0)].map((_, i) => (
                          <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />
                        ))}
                      </div>

                      <blockquote className="text-lg lg:text-xl text-gray-700 leading-relaxed italic text-center lg:text-left">
                        "{testimonials[currentIndex]?.text}"
                      </blockquote>
                    </div>
                  </div>
                </div>

                <div className="absolute bottom-0 right-0 w-32 h-32 bg-primary-50 rounded-full transform translate-x-16 translate-y-16"></div>
              </div>

              <div className="flex justify-center items-center mt-8 space-x-4">
                <button
                  onClick={prevTestimonial}
                  className="bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 text-gray-600 hover:text-primary-600 p-3 rounded-full transition-all duration-300"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>

                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentIndex
                        ? 'bg-primary-600 scale-125'
                        : 'bg-gray-300 hover:bg-primary-400'
                        }`}
                    />
                  ))}
                </div>

                <button
                  onClick={nextTestimonial}
                  className="bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 text-gray-600 hover:text-primary-600 p-3 rounded-full transition-all duration-300"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="hidden lg:flex justify-center mt-8 space-x-4">
                {testimonials.map((testimonial, index) => (
                  <button
                    key={testimonial.id}
                    onClick={() => setCurrentIndex(index)}
                    className={`flex-shrink-0 p-2 rounded-2xl transition-all duration-300 ${index === currentIndex
                      ? 'bg-primary-100 border-2 border-primary-300'
                      : 'bg-white border-2 border-transparent hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={testimonial.image_url}
                        alt={`${testimonial.name} - Testimonial Thumbnail`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="text-left">
                        <div className="text-sm font-semibold text-gray-900">
                          {testimonial.name}
                        </div>
                        <div className="text-xs text-gray-600">
                          {testimonial.location}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9. Static Blog Section
// -------------------------------------------------------------
const StaticBlogSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const videoData = {
    title: 'How to Apply for AHAM Home Loan in 5 Easy Steps',
    views: '2.5K',
    description: 'Watch our step-by-step guide on how to apply for a home loan with AHAM Housing Finance. Learn about documentation, eligibility, and the approval process.',
    thumbnail: '/img/Blogs/Loan2.webp',
    link: 'https://www.youtube.com/@ahamhfc'
  };

  const blogPosts = [
    {
      id: 1,
      title: "Home Purchase Loan: Your Complete Guide to Buying Your Dream Home",
      excerpt: "Everything you need to know about securing a home purchase loan - from eligibility to approval, make your dream home a reality.",
      category: "Home Purchase or Resale Loan",
      author: "Rajesh Kumar",
      date: "Jan 15, 2025",
      read_time: "8 min read",
      image_url: "img/Blogs/home-purchase-loan.png"
    },
    {
      id: 2,
      title: "Home Construction Loan: Build Your Dream Home from Ground Up",
      excerpt: "Comprehensive guide to construction loans - finance your dream home construction with flexible disbursement and competitive rates.",
      category: "Home Construction Loan",
      author: "Priya Sharma",
      date: "Jan 12, 2025",
      read_time: "9 min read",
      image_url: "img/Blogs/home-construction-loan.png"
    },
    {
      id: 3,
      title: "Home Renovation Loan: Transform Your Living Space",
      excerpt: "Upgrade, renovate, and modernize your home with specialized renovation loans - from kitchen remodeling to complete makeovers.",
      category: "Home Renovation Loan",
      author: "Amit Patel",
      date: "Jan 10, 2025",
      read_time: "7 min read",
      image_url: "img/Blogs/home-renovation-loan.png"
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {t('blog.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('blog.subtitle')}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            className="space-y-8"
          >
            {blogPosts.map((post) => (
              <motion.article
                key={post.id}
                variants={itemVariants}
                onClick={() => navigate(`/home-loan-blog-aham-housing-finance/${post.id}`)}
                className="group cursor-pointer block"
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="bg-white rounded-2xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/3 h-48 md:h-auto relative overflow-hidden">
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat transform group-hover:scale-110 transition-transform duration-700"
                        style={{ backgroundImage: `url(${post.image_url.startsWith('http') ? post.image_url : '/' + post.image_url})` }}
                        role="img"
                        aria-label={post.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-primary-600 px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    <div className="md:w-2/3 p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{post.read_time}</span>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>

                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>

                      <button className="flex items-center space-x-2 text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                        <span>Read More</span>
                        <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          <motion.div
            variants={containerVariants}
            className="space-y-8"
          >
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-soft overflow-hidden"
            >
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-primary-600 to-purple-600 relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50"
                    style={{
                      backgroundImage: `url(${videoData.thumbnail})`
                    }}
                    role="img"
                    aria-label={`Video Thumbnail: ${videoData.title}`}
                  />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={() => window.open(videoData.link, '_blank')}
                      className="group bg-white rounded-full p-4 shadow-lg hover:scale-110 transition-transform duration-300"
                      aria-label="Play Video"
                    >
                      <FaYoutube className="h-12 w-12 text-red-600 group-hover:text-red-700 transition-colors" />
                    </button>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-white font-semibold mb-2 line-clamp-2">
                      {videoData.title}
                    </h4>
                    <div className="flex items-center space-x-4 text-white/80 text-sm">
                      <span>AHAM Housing Finance</span>
                      <span>•</span>
                      <span>{videoData.views} views</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-600 leading-relaxed line-clamp-3">
                  {videoData.description}
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <Link
            to="/home-loan-blog-aham-housing-finance"
            className="btn bg-[#ed2636] px-6 py-3 text-white rounded-lg hover:bg-primary-600 hover:text-white transition inline-block"
          >
            View All Articles
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9.7. Static Beyond Calculator Section
// -------------------------------------------------------------
const StaticBeyondCalculatorSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [settings, setSettings] = useState({
    tag_text: 'Beyond Numbers',
    title_text: 'How Aham Looks Beyond the Calculator',
    description_text: 'Numbers do not tell the full story. Our team meets you at your doorstep to understand your real income, even without a formal payslip.',
    image_url: 'img/Scorecard/Calculator.png',
    card_title: 'Doorstep Assessment',
    card_subtitle: 'Personalized income evaluation'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('/api/admin/eligibility-scorecard/beyond-numbers');
        const json = await response.json();
        if (json.success && json.data) {
          setSettings(json.data);
        }
      } catch (error) {
        console.error('Error fetching beyond numbers settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100, damping: 15 } }
  };

  return (
    <section className="py-24 bg-white relative overflow-hidden border-t border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white -z-10"></div>

      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="bg-gradient-to-br from-[#0A1D36] to-[#1a365d] rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden shadow-2xl"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#ed2636] rounded-full mix-blend-screen filter blur-[100px] opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>

          <div className="flex flex-col lg:flex-row items-center gap-12 relative z-10">
            <motion.div variants={itemVariants} className="w-full lg:w-1/2 text-left">
              <div className="inline-block bg-[#D4AF37]/20 text-[#D4AF37] px-4 py-2 rounded-full font-semibold text-sm tracking-wider uppercase mb-6 border border-[#D4AF37]/30">
                {settings.tag_text}
              </div>
              <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                {settings.title_text.split('Beyond the Calculator').length > 1 ? (
                  <>
                    {settings.title_text.split('Beyond the Calculator')[0]}
                    <span className="text-[#D4AF37]">Beyond the Calculator</span>
                    {settings.title_text.split('Beyond the Calculator')[1]}
                  </>
                ) : (
                  settings.title_text
                )}
              </h2>
              <p className="text-lg md:text-xl text-gray-300 leading-relaxed font-light border-l-4 border-[#D4AF37] pl-6 py-2">
                {settings.description_text}
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="w-full lg:w-1/2">
              <div className="relative group">
                <div className="absolute -inset-2 bg-gradient-to-r from-[#D4AF37] to-[#ed2636] rounded-3xl opacity-30 group-hover:opacity-60 blur-lg transition duration-700"></div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white p-2">
                  <img
                    src={settings.image_url ? (settings.image_url.startsWith('http') ? settings.image_url : `/${settings.image_url}`) : "/img/Scorecard/Calculator.png"}
                    alt="Team meeting at doorstep"
                    className="w-full h-full object-cover rounded-2xl transform group-hover:scale-105 transition duration-700 aspect-video"
                  />
                  <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md p-4 rounded-xl shadow-lg transform translate-y-2 group-hover:translate-y-0 opacity-90 group-hover:opacity-100 transition duration-500">
                    <div className="flex items-center gap-4">
                      <div className="bg-[#0A1D36] p-3 rounded-full text-white">
                        <HomeIcon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 text-sm">{settings.card_title}</p>
                        <p className="text-xs text-gray-500">{settings.card_subtitle}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9.5. Static Max Loan Eligibility Section
// -------------------------------------------------------------
const StaticMaxLoanEligibilitySection = () => {
  const [tableData, setTableData] = useState([]);
  const [settings, setSettings] = useState({
    section_title: "Calculate Your Maximum Home Loan Eligibility",
    section_subtitle: "Use this maximum home loan eligibility calculator range for each loan type:",
    footer_text: "Total loans with Aham cannot cross ₹50 Lakhs. Your final amount depends on your full application."
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [rowsRes, settingsRes] = await Promise.all([
          fetch('/api/admin/eligibility-scorecard/max-loan/rows/active'),
          fetch('/api/admin/eligibility-scorecard/max-loan/settings')
        ]);

        const rowsData = await rowsRes.json();
        const settingsData = await settingsRes.json();

        if (rowsData.success) {
          setTableData(rowsData.data);
        }
        if (settingsData.success && settingsData.data) {
          setSettings(settingsData.data);
        }
      } catch (error) {
        console.error('Error fetching max loan data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <section className="py-20 bg-white border-t border-gray-100">
      <div className="container mx-auto px-4 max-w-6xl">
        <h2 className="text-3xl md:text-4xl font-bold text-[#0A1D36] mb-4 border-b-2 border-[#D4AF37] pb-2 inline-block">
          {settings.section_title}
        </h2>
        <p className="text-gray-700 text-lg mb-8">
          {settings.section_subtitle}
        </p>

        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-full"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
            <div className="h-10 bg-gray-200 rounded w-full"></div>
          </div>
        ) : (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#0A1D36] text-white">
                  <th className="p-4 font-bold border border-[#0A1D36] border-b-0 text-[15px]">Loan Type</th>
                  <th className="p-4 font-bold border border-[#0A1D36] border-b-0 text-[15px]">Loan Amount Range</th>
                  <th className="p-4 font-bold border border-[#0A1D36] border-b-0 text-[15px]">Max Tenure</th>
                  <th className="p-4 font-bold border border-[#0A1D36] border-b-0 text-[15px]">Max Loan-to-Value</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="p-4 text-center text-gray-500">No data available</td>
                  </tr>
                ) : (
                  tableData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-[#F5F3EC]"}>
                      <td className="p-4 border border-gray-200 text-gray-800 font-medium text-[15px]">{row.loan_type}</td>
                      <td className="p-4 border border-gray-200 text-gray-800 text-[15px]">{row.amount_range}</td>
                      <td className="p-4 border border-gray-200 text-gray-800 text-[15px]">{row.max_tenure}</td>
                      <td className="p-4 border border-gray-200 text-gray-800 text-[15px]">{row.max_ltv}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-8 text-gray-700 font-medium text-lg">
          {settings.footer_text}
        </p>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9.6. Dynamic Eligibility Factors & Tips Section
// -------------------------------------------------------------
const StaticEligibilityFactorsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [factors, setFactors] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({
    section_title: 'Factors Affecting Your Home Loan Eligibility',
    section_subtitle: 'Understanding these key factors will help you improve your eligibility and secure the best loan amount.'
  });
  const [tips, setTips] = useState([]);
  const [tipsSettings, setTipsSettings] = useState({
    section_title: 'Tips to Increase Your Home Loan Eligibility',
    image_url: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
  });
  const [loading, setLoading] = useState(true);

  const IconMap = {
    BanknotesIcon,
    DocumentCheckIcon,
    ChartBarIcon,
    BuildingOfficeIcon,
    ClockIcon,
    HomeIcon,
    CheckCircleIcon,
    ShieldCheckIcon,
    UsersIcon,
    CurrencyRupeeIcon,
    RocketLaunchIcon,
    TrophyIcon,
    LightBulbIcon
  };

  const defaultFactors = [
    { id: 1, title: "Monthly income", description: "more income, more eligibility", icon_name: "BanknotesIcon", color_class: "text-green-500", bg_class: "bg-green-50" },
    { id: 2, title: "Existing dues", description: "fewer dues, more room to borrow", icon_name: "DocumentCheckIcon", color_class: "text-red-500", bg_class: "bg-red-50" },
    { id: 3, title: "Credit score", description: "700 or above is good", icon_name: "ChartBarIcon", color_class: "text-blue-500", bg_class: "bg-blue-50" },
    { id: 4, title: "Job stability", description: "2 years salaried, 3 years self-employed", icon_name: "BuildingOfficeIcon", color_class: "text-purple-500", bg_class: "bg-purple-50" },
    { id: 5, title: "Loan tenure", description: "a longer tenure can raise your amount", icon_name: "ClockIcon", color_class: "text-orange-500", bg_class: "bg-orange-50" },
    { id: 6, title: "Property value", description: "sets your loan-to-value limit", icon_name: "HomeIcon", color_class: "text-teal-500", bg_class: "bg-teal-50" }
  ];

  const defaultTips = [
    "Pay existing dues on time to keep your CIBIL score healthy.",
    "Close small loans before you apply.",
    "Add a co-applicant, such as your spouse or parent.",
    "Save for a bigger own contribution.",
    "Choose a longer tenure for a lower monthly repayment.",
    "Tell us about rental, farm or pension income."
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [factorsRes, settingsRes, tipsRes, tipsSettingsRes] = await Promise.all([
          fetch('/api/admin/eligibility-scorecard/factors/active'),
          fetch('/api/admin/eligibility-scorecard/factors/settings'),
          fetch('/api/admin/eligibility-scorecard/tips/active'),
          fetch('/api/admin/eligibility-scorecard/tips/settings')
        ]);

        const factorsJson = await factorsRes.json();
        const settingsJson = await settingsRes.json();
        const tipsJson = await tipsRes.json();
        const tipsSettingsJson = await tipsSettingsRes.json();

        if (factorsJson.success && factorsJson.data.length > 0) {
          setFactors(factorsJson.data);
        } else {
          setFactors(defaultFactors);
        }

        if (settingsJson.success && settingsJson.data) {
          setSectionSettings(prev => ({
            ...prev,
            section_title: settingsJson.data.section_title || prev.section_title,
            section_subtitle: settingsJson.data.section_subtitle || prev.section_subtitle
          }));
        }

        if (tipsJson.success && tipsJson.data.length > 0) {
          setTips(tipsJson.data.map(t => t.tip_text));
        } else {
          setTips(defaultTips);
        }

        if (tipsSettingsJson.success && tipsSettingsJson.data) {
          setTipsSettings(prev => ({
            ...prev,
            section_title: tipsSettingsJson.data.section_title || prev.section_title,
            image_url: tipsSettingsJson.data.image_url || prev.image_url
          }));
        }
      } catch (error) {
        console.error('Error fetching eligibility factors & tips:', error);
        setFactors(defaultFactors);
        setTips(defaultTips);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-24 bg-gray-50 overflow-hidden relative border-t border-gray-100">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary-100/50 blur-3xl mix-blend-multiply" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-blue-100/50 blur-3xl mix-blend-multiply" />
      </div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="space-y-32"
        >
          {/* Section 1: Factors - Dynamic */}
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div variants={itemVariants} className="w-full lg:w-1/2 relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary-600 to-blue-500 rounded-3xl opacity-20 group-hover:opacity-40 blur-lg transition duration-700"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img
                  src={sectionSettings.image_url ? (sectionSettings.image_url.startsWith('http') ? sectionSettings.image_url : `/${sectionSettings.image_url}`) : "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"}
                  alt="Factors Affecting Eligibility"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
                {sectionSettings.section_title.split('Home Loan Eligibility').length > 1 ? (
                  <>
                    {sectionSettings.section_title.split('Home Loan Eligibility')[0]}
                    <span className="text-primary-600">Home Loan Eligibility</span>
                    {sectionSettings.section_title.split('Home Loan Eligibility')[1]}
                  </>
                ) : (
                  sectionSettings.section_title
                )}
              </h2>
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="animate-pulse flex items-start p-5 rounded-2xl bg-white border border-gray-100">
                      <div className="w-12 h-12 bg-gray-200 rounded-xl mr-4 flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded mb-2"></div>
                        <div className="h-3 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {factors.map((factor, idx) => {
                    const Icon = IconMap[factor.icon_name] || BanknotesIcon;
                    return (
                      <motion.div
                        key={factor.id || idx}
                        whileHover={{ y: -5 }}
                        className="flex items-start p-5 rounded-2xl bg-white shadow-soft hover:shadow-large transition-all duration-300 border border-gray-100"
                      >
                        <div className={`${factor.bg_class} p-3 rounded-xl mr-4 flex-shrink-0`}>
                          <Icon className={`w-6 h-6 ${factor.color_class}`} />
                        </div>
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1">{factor.title}</h4>
                          <p className="text-sm text-gray-600 leading-relaxed font-medium">{factor.description}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>

          {/* Section 2: Tips */}
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            <motion.div variants={itemVariants} className="w-full lg:w-1/2 relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-green-500 to-teal-400 rounded-3xl opacity-20 group-hover:opacity-40 blur-lg transition duration-700"></div>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img
                  src={tipsSettings.image_url ? (tipsSettings.image_url.startsWith('http') ? tipsSettings.image_url : `/${tipsSettings.image_url}`) : "https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"}
                  alt="Tips to Increase Eligibility"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="w-full lg:w-1/2">
              <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-8 leading-tight">
                {tipsSettings.section_title.split('Home Loan Eligibility').length > 1 ? (
                  <>
                    {tipsSettings.section_title.split('Home Loan Eligibility')[0]}
                    <span className="text-green-600">Home Loan Eligibility</span>
                    {tipsSettings.section_title.split('Home Loan Eligibility')[1]}
                  </>
                ) : (
                  tipsSettings.section_title
                )}
              </h2>
              <div className="space-y-4">
                {tips.map((tip, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ x: 5 }}
                    className="flex items-center p-5 rounded-2xl bg-white shadow-soft hover:shadow-large transition-all duration-300 border border-gray-100"
                  >
                    <div className="bg-green-50 p-2.5 rounded-full mr-4 flex-shrink-0">
                      <CheckCircleIcon className="w-7 h-7 text-green-600" />
                    </div>
                    <span className="text-gray-700 font-semibold text-lg">{tip}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 9.8. Static FAQ Section
// -------------------------------------------------------------
const StaticFAQSection = () => {
  const [activeCategory, setActiveCategory] = useState("About the Calculator");
  const [openIndex, setOpenIndex] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [settings, setSettings] = useState({
    tag_text: 'Got Questions?',
    title_text: 'Frequently Asked Questions',
    subtitle_text: 'Find quick answers to common queries about our eligibility calculator and home loan process.'
  });
  const [faqsData, setFaqsData] = useState({
    "About the Calculator": [
      { q: "What is a home loan eligibility calculator?", a: "A free tool that estimates your home loan amount from your income and dues." },
      { q: "How is home loan eligibility calculated?", a: "Your income, minus your dues, spread across your loan tenure." },
      { q: "Is the calculator free?", a: "Yes, and no documents are needed to use it." },
      { q: "How accurate is the eligibility calculator?", a: "It gives a close estimate. Your final amount needs a full assessment." }
    ],
    "Income & Eligibility": [
      { q: "Can I calculate home loan eligibility based on salary?", a: "Yes. Enter your monthly salary and dues to get an estimate." },
      { q: "What is the minimum salary required for a home loan?", a: "₹15,000 a month for salaried applicants, ₹80,000 a month for NRIs." },
      { q: "Can self-employed applicants use the calculator?", a: "Yes. Enter your average monthly cash profit instead of salary." },
      { q: "Can I increase my home loan eligibility?", a: "Yes. Add a co-applicant, raise your credit score, or choose a longer tenure." }
    ],
    "Credit Score & Co-applicants": [
      { q: "Does CIBIL score affect home loan eligibility?", a: "Yes. A score of 700 or above is good for your application." },
      { q: "Can I apply with a co-applicant?", a: "Yes. Spouse, parent-son and eligible sibling incomes can be combined." },
      { q: "Does using the calculator affect my credit score?", a: "No. It does not run a credit check." }
    ],
    "Next Steps & Documents": [
      { q: "What documents are required after checking eligibility?", a: "ID proof, income proof and property documents." }
    ]
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, faqsRes] = await Promise.all([
          fetch('/api/admin/eligibility-scorecard/faqs/settings'),
          fetch('/api/admin/eligibility-scorecard/faqs/active')
        ]);
        const settingsJson = await settingsRes.json();
        const faqsJson = await faqsRes.json();

        if (settingsJson.success && settingsJson.data) {
          setSettings(settingsJson.data);
        }
        if (faqsJson.success && faqsJson.data.length > 0) {
          // Group by category
          const grouped = {};
          faqsJson.data.forEach(item => {
            const cat = item.category;
            if (!grouped[cat]) {
              grouped[cat] = [];
            }
            grouped[cat].push({ q: item.question, a: item.answer });
          });
          setFaqsData(grouped);

          // Set first category active
          const categories = Object.keys(grouped);
          if (categories.length > 0) {
            setActiveCategory(categories[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setOpenIndex(0);
  }, [activeCategory]);

  const categories = Object.keys(faqsData);

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden border-t border-gray-200">
      {/* Decorative background shapes */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-primary-100 rounded-full blur-[100px] opacity-60"></div>
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-[#D4AF37] rounded-full blur-[120px] opacity-20"></div>

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Top Centered Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <div className="inline-block bg-white shadow-sm border border-gray-100 px-4 py-2 rounded-full mb-6">
            <span className="text-primary-600 font-bold text-sm tracking-wider uppercase flex items-center gap-2 justify-center">
              <LightBulbIcon className="w-5 h-5 text-[#D4AF37]" />
              {settings.tag_text}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#0A1D36] mb-6 leading-tight">
            {settings.title_text.split('Questions').length > 1 ? (
              <>
                {settings.title_text.split('Questions')[0]}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-[#D4AF37]">Questions</span>
                {settings.title_text.split('Questions')[1]}
              </>
            ) : (
              settings.title_text
            )}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {settings.subtitle_text}
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-16 items-start">

          {/* Left column: Sticky Categories */}
          <div className="w-full lg:w-1/3 lg:sticky lg:top-32">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="flex flex-row overflow-x-auto lg:overflow-x-visible lg:flex-col space-x-4 lg:space-x-0 lg:space-y-3 pb-4 lg:pb-0 scrollbar-hide">
                {categories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveCategory(cat)}
                    className={`whitespace-nowrap lg:whitespace-normal text-left px-6 py-4 rounded-2xl transition-all duration-300 font-bold text-sm md:text-base border-2 ${activeCategory === cat ? 'border-primary-600 bg-primary-50 text-primary-600 shadow-md transform lg:scale-105' : 'border-transparent text-gray-500 bg-white/50 hover:bg-white hover:text-gray-900'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right column: Accordion */}
          <div className="w-full lg:w-2/3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-4"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeCategory}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {faqsData[activeCategory] && faqsData[activeCategory].map((faq, index) => {
                    const isOpen = openIndex === index;
                    return (
                      <motion.div
                        key={index}
                        initial={false}
                        animate={{ backgroundColor: isOpen ? "rgba(255, 255, 255, 1)" : "rgba(255, 255, 255, 0.6)" }}
                        className={`border border-gray-200 rounded-2xl overflow-hidden transition-all duration-300 ${isOpen ? 'shadow-lg ring-1 ring-primary-100' : 'hover:border-primary-300 hover:bg-white'}`}
                      >
                        <button
                          onClick={() => setOpenIndex(isOpen ? null : index)}
                          className="w-full px-6 py-5 flex items-center justify-between focus:outline-none text-left"
                        >
                          <span className={`text-lg font-bold pr-4 transition-colors duration-300 ${isOpen ? 'text-primary-600' : 'text-gray-800'}`}>
                            {faq.q}
                          </span>
                          <div className={`flex-shrink-0 ml-2 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-primary-50 text-primary-600 rotate-180' : 'bg-gray-50 text-gray-400'}`}>
                            <ChevronRightIconOutline className={`w-5 h-5 transition-transform duration-300 ${isOpen ? '-rotate-90' : 'rotate-90'}`} />
                          </div>
                        </button>

                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                            >
                              <div className="px-6 pb-6 pt-0 border-t border-gray-50">
                                <p className="text-gray-600 text-lg leading-relaxed mt-4">
                                  {faq.a}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>

        </div>
      </div>
    </section>
  );
};

// -------------------------------------------------------------
// 10. Main Page Component Wrapper
// -------------------------------------------------------------
const ScorecardPage = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question", "name": "What is a home loan eligibility calculator?",
        "acceptedAnswer": { "@type": "Answer", "text": "A free tool that estimates your home loan amount from your income and dues." }
      },
      {
        "@type": "Question", "name": "How is home loan eligibility calculated?",
        "acceptedAnswer": { "@type": "Answer", "text": "Your income, minus your dues, spread across your loan tenure." }
      },
      {
        "@type": "Question", "name": "Can I calculate home loan eligibility based on salary?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Enter your monthly salary and dues to get an estimate." }
      },
      {
        "@type": "Question", "name": "What is the minimum salary required for a home loan?",
        "acceptedAnswer": { "@type": "Answer", "text": "₹15,000 a month for salaried applicants, ₹80,000 a month for NRIs." }
      },
      {
        "@type": "Question", "name": "Can self-employed applicants use the calculator?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Enter your average monthly cash profit instead of salary." }
      },
      {
        "@type": "Question", "name": "Does CIBIL score affect home loan eligibility?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. A score of 700 or above is good for your application." }
      },
      {
        "@type": "Question", "name": "Can I apply with a co-applicant?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Spouse, parent-son and eligible sibling incomes can be combined." }
      },
      {
        "@type": "Question", "name": "Is the calculator free?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, and no documents are needed to use it." }
      },
      {
        "@type": "Question", "name": "Does using the calculator affect my credit score?",
        "acceptedAnswer": { "@type": "Answer", "text": "No. It does not run a credit check." }
      },
      {
        "@type": "Question", "name": "How accurate is the eligibility calculator?",
        "acceptedAnswer": { "@type": "Answer", "text": "It gives a close estimate. Your final amount needs a full assessment." }
      },
      {
        "@type": "Question", "name": "What documents are required after checking eligibility?",
        "acceptedAnswer": { "@type": "Answer", "text": "ID proof, income proof and property documents." }
      },
      {
        "@type": "Question", "name": "Can I increase my home loan eligibility?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes. Add a co-applicant, raise your credit score, or choose a longer tenure." }
      }
    ]
  };

  return (
    <>
      <SEO
        title="Home Loan Eligibility Calculator | Check Online - Aham HFC"
        description="Check your home loan eligibility online with Aham HFC. Get an estimated loan amount based on your income, employment type and family income combined."
        schema={faqSchema}
        canonical="https://ahamhfc.com/home-loan-eligibility-calculator"
      />
      <main className="overflow-hidden">
        <StaticHeroSection />
        <StaticEligibilityCardsSection />
        {/* <StaticEligibilityScorecardSection /> */}
        <EligibilityScorecardSection pageType="scorecard_page" />

        <StaticMaxLoanEligibilitySection />
        <StaticEligibilityFactorsSection />
        <StaticBeyondCalculatorSection />
        <StaticWhyChooseSection />
        <StaticFAQSection />
        <StaticProductsSection />


        {/* <StaticEMICalculatorSection /> */}
        {/* <StaticTestimonialsSection />
        <StaticBlogSection /> */}
        <StaticImportantNewsSection />
      </main>
    </>
  );
};

export default ScorecardPage;
